import React, { Fragment } from 'react';
import HelmetWrap from 'components/helmet-wrap';
import { DI } from 'modules';
import frontload from 'hoc/frontload';
import Component from 'components/component';
import page from 'hoc/page';
import AcceptAgreementsModals from 'components/accept-agreements-modals';

import Preview from 'pages/content/components/preview';
import LiveDescription from './live-description';

import LiveStreamInfo from './live-stream-info';
import { ActionCableConsumer } from 'components/action-cable-wrapper';

import HeaderView from 'components/header/view';

import * as routes from 'config/routes';

import NotFoundLivePage from '../not-found';
import { prepareUrl } from 'helpers/strings';

import { VideoItemAdapter } from 'components/list';

import PageLoading from 'components/page-loading';
import { cyrillicTitle } from 'helpers/strings';

import 'submodules/responsiveruptly/src/less/live.less';
import 'submodules/responsiveruptly/src/less/plan-details.less';
import 'submodules/responsiveruptly/src/less/plan.less';

class Base extends Component {
    get path() {
        return '/' + this.props.common.locale + '/lives/' + this.live.slug;
    }
    get link() {
        return document.location.origin + this.path;
    }
}

const customOffset = {
    margin: '0 0 0 30px',
    height: 'auto'
};

export class LiveView extends Base {
    static preload(props) {
        const slug = LiveView.getSlug(props.match.params.id);

        return Promise.all([
            props.actions.targets.getProviders(),
            props.actions.lives.getLive(slug).then((resp) => {
                if (props.user.isSignedIn) {
                    return Promise.all([
                        props.actions.user.getActiveAgreements({
                            content_type: 'live',
                            content_id: resp.body.id,
                        }),
                        props.actions.user.getAgreements(),
                        props.actions.targets.getTargets(slug),
                    ]);
                } else {
                    return resp;
                }
            }),
        ]);
    }

    static getSlug(pathID) {
        let [slug] = pathID.match(/(\d+(-\w+\d+)?)/) || [];
        return slug;
    }

    state = {
        selectedLanguageID: null,
        invalid: false,
    };

    constructor(props) {
        super(props);

        if (this.live.languages.length) {
            this.state.selectedLanguageID = this.selectDefaultLanguageId(
                this.live.languages
            );
        }
    }

    componentDidUpdate(prevProps) {
        const slug = LiveView.getSlug(this.props.match.params.id);
        const prevSlug = LiveView.getSlug(prevProps.match.params.id);
        const currentPayload = this.props.lives.byID.getValue(slug).payload;
        const prevPayload = prevProps.lives.byID.getValue(slug).payload;

        if (currentPayload.languages.length && !prevPayload.languages.length) {
            this.setState({
                selectedLanguageID: this.selectDefaultLanguageId(
                    currentPayload.languages
                ),
            });
        }

        const liveIdIsUpdated = slug !== prevSlug;
        const userIsUpdated =
            this.props.user.isSignedIn !== prevProps.user.isSignedIn;
        const localeIsUpdated =
            this.props.common.locale !== prevProps.common.locale;

        if (liveIdIsUpdated || userIsUpdated || localeIsUpdated) {
            LiveView.preload(this.props)
                .then(() => {
                    this.setState({ invalid: false });
                })
                .catch((error) => {
                    this.setState({ invalid: true });
                    throw error;
                });
        }
    }

    selectDefaultLanguageId(languages) {
        const slug = LiveView.getSlug(this.props.match.params.id);
        if (languages.length === 1) {
            return languages[0].id;
        } else {
            const targets = this.props.targets.list.getValue(slug).payload;
            const [firstWithTargets] = languages.filter((item) =>
                targets.hasOwnProperty(item.id)
            );

            if (firstWithTargets) {
                return firstWithTargets.id;
            }
        }
        return null;
    }

    get live() {
        const slug = LiveView.getSlug(this.props.match.params.id);

        return this.props.lives.byID.getValue(slug).payload;
    }

    get pending() {
        const slug = LiveView.getSlug(this.props.match.params.id);
        const node = this.props.lives.byID.getValue(slug);
        return node.pending;
    }

    onReceivedWebsocket = (msg) => {
        this.props.actions.lives.wsStatusChangeView(msg.data);
    };

    onSelectLanguage = (ev, selectedLanguageID) => {
        this.setState({ selectedLanguageID });
    };

    string = (key) => {
        return this.props.common.strings[`page.lives.view.${key}`];
    };

    goToLives = () => {
        this.props.history.push(`/${this.props.common.locale}`);
    };

    onAcceptAgreement = () => {
        const liveEvent = this.live;
        this.props.actions.user.getActiveAgreements({
            content_type: 'live',
            content_id: liveEvent.id,
        });
    };

    getImgUrl = (size) => {
        const img = this.live.image;

        return (img[size] && img[size].url) || img.url;
    };

    render() {
        const liveEvent = this.live;
        const slug = LiveView.getSlug(this.props.match.params.id);
        const isFinished = liveEvent.status === 'finished';

        if (
            this.props.common.notFound.includes(
                `/${this.props.common.locale}/events/${slug}`
            )
        ) {
            return <NotFoundLivePage />;
        }

        let pathToAdmin = `${document.location.hostname}/cms/#/lives/events/edit/${slug}`;
        let pathToAdminView = `${document.location.hostname}/cms/#/lives/events/preview/${slug}`;

        const { meta_tag } = liveEvent;

        if (!this.live.id) {
            return <PageLoading />;
        }

        const currentID = this.props.match.params.id;
        const validID = `${liveEvent.slug}-${cyrillicTitle(liveEvent.title)}`;
        const canonical = this.props.match.url.replace(currentID, validID);

        const liveStreamInfo = (
            <LiveStreamInfo
                liveEvent={liveEvent}
                selectedLanguageID={this.state.selectedLanguageID}
                onSelectLanguage={this.onSelectLanguage}
            />
        );

        const hasRelatedVideos =
            liveEvent.video_projects && liveEvent.video_projects.length > 0;

        return (
            <section className="content">
                <ActionCableConsumer
                    channel="Content::LiveEventsChannel"
                    onReceived={this.onReceivedWebsocket}
                />
                <HelmetWrap
                    {...(meta_tag || {})}
                    live={liveEvent}
                    page="events"
                    canonical={canonical}
                />

                <AcceptAgreementsModals
                    onAccept={this.onAcceptAgreement}
                    acceptOnly
                />
                <div className="wrap">
                    <HeaderView
                        liveEvent={liveEvent}
                        buttonBackNameOfStr={'page.lives.view.all_lives'}
                        getImgUrl={this.getImgUrl}
                        defaultBackUrl={routes.livesList.getPath(
                            this.props.common.locale
                        )}
                        alwaysBackToDefault={true}
                        editButton={(props) => (
                            <a
                                href={prepareUrl(pathToAdmin)}
                                target="_blank"
                                style={customOffset}
                                className="text-button"
                            >
                                Edit
                            </a>
                        )}
                        viewButton={(props) => (
                            <a
                                href={prepareUrl(pathToAdminView)}
                                target="_blank"
                                style={customOffset}
                                className="text-button"
                            >
                                View
                            </a>
                        )}
                        render={(props) => null}
                    />
                    {isFinished && hasRelatedVideos && (
                        <h1 className="live-title">
                            {
                                this.props.common.strings[
                                    'page.lives.view.finished_event'
                                ]
                            }
                        </h1>
                    )}
                    <div className="live">
                        <div className="live-column">
                            <Preview
                                entity={liveEvent}
                                isLive={true}
                                isOnLive={liveEvent.status === 'live'}
                                selectedLanguageID={
                                    this.state.selectedLanguageID
                                }
                                preview={this.getImgUrl('v960x540')}
                                time={liveEvent.start_at}
                                watermark={false}
                            />
                            <LiveDescription liveEvent={liveEvent} />
                            {isFinished && hasRelatedVideos
                                ? liveStreamInfo
                                : null}
                        </div>
                        <div className="live-column">
                            {!isFinished || !hasRelatedVideos ? (
                                liveStreamInfo
                            ) : (
                                <div className="live-footages">
                                    <h2 className="live-title">
                                        {
                                            this.props.common.strings[
                                                'page.lives.view.footages'
                                            ]
                                        }
                                    </h2>
                                    <div className="live-footages__list">
                                        {hasRelatedVideos &&
                                            liveEvent.video_projects.map(
                                                (video, index, array) => (
                                                    <VideoItemAdapter
                                                        key={video.id}
                                                        video={video}
                                                        view={null}
                                                        modificator={
                                                            array.length === 1
                                                                ? 'single'
                                                                : null
                                                        }
                                                    />
                                                )
                                            )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* {Boolean(
                        liveEvent.video_projects &&
                            liveEvent.video_projects.length
                    ) && (
                        <DashboardSliderContainer
                            title={
                                this.props.common.strings[
                                    'page.lives.view.related.title'
                                ]
                            }
                            text={
                                this.props.common.strings[
                                    'page.lives.view.related.text'
                                ]
                            }
                        >
                            <DashboardSlider items={liveEvent.video_projects} />
                        </DashboardSliderContainer>
                    )} */}
                </div>
            </section>
        );
    }
}

export default page(
    DI(['common', 'lives', 'user', 'targets'])(frontload(LiveView))
);
